const STORAGE_KEY = 'oudh:welcome-popup';
const SKIP_PATHS = /^\/(cart|checkouts?|account|password)(\/|$)/;

/**
 * Welcome popup: shows the discount code and a newsletter form.
 *
 * State (localStorage): `snoozeUntil` after a dismissal, `subscribed` for good after signup.
 * While snoozed a small "Get 10% off" pill stays in the corner so the offer is never lost.
 * The form posts in the background so the popup can confirm in place.
 *
 * Plain listeners rather than `on:click`: the dialog must only close when the backdrop itself is
 * clicked, and Horizon's declarative handlers run for clicks on anything inside the element.
 */
class OudhWelcomePopup extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  /** Replaced with the real elements in connectedCallback. */
  dialog = document.createElement('dialog');
  form = document.createElement('form');

  /** @type {Element | null} */
  teaser = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const form = this.querySelector('form');
    if (!(dialog instanceof HTMLDialogElement) || !(form instanceof HTMLFormElement)) return;

    this.dialog = dialog;
    this.form = form;
    this.teaser = this.querySelector('[data-teaser]');
    this.#watchStickyBar();

    this.addEventListener('click', (event) => this.#onClick(event));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => this.#onClose());
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#subscribe();
    });

    if (window.Shopify?.designMode) {
      this.#open();
      return;
    }

    const state = this.#read();
    if (state.subscribed || SKIP_PATHS.test(window.location.pathname)) return;

    if (state.snoozeUntil > Date.now()) {
      if (!state.teaserHidden) this.#showTeaser();
      return;
    }

    // Lazy images inside a closed dialog never load, so fetch it during the delay.
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });

    this.#timer = window.setTimeout(() => this.#tryOpen(), Number(this.dataset.delay ?? 5) * 1000);
  }

  /**
   * Resolves once Shopify's cookie banner is out of the way, so the two never compete for the screen.
   * Resolves at once when there is no banner or the consent API is unavailable.
   * @returns {Promise<void>}
   */
  #consentSettled() {
    return new Promise((resolve) => {
      const shopify = /** @type {any} */ (window.Shopify);
      if (!shopify?.loadFeatures) return resolve();

      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {unknown} */ error) => {
        const privacy = shopify.customerPrivacy;
        const pending = !error && privacy?.shouldShowBanner?.() && privacy.currentVisitorConsent?.().preferences === '';
        if (!pending) return resolve();
        document.addEventListener('visitorConsentCollected', () => resolve(), { once: true });
      });
    });
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#barObserver?.disconnect();
    this.#barResize?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #barObserver;

  /** @type {ResizeObserver | undefined} */
  #barResize;

  /**
   * Product page only: the reminder pill keeps clear of the sticky add-to-cart bar while it shows,
   * publishing the bar's height as `--oudh-welcome-lift` for the pill's CSS. Same rule WhatsApp follows.
   */
  #watchStickyBar() {
    const bar = document.querySelector('oudh-sticky-cart');
    if (!(bar instanceof HTMLElement)) return;

    const update = () => {
      const lift = bar.hasAttribute('data-visible') ? bar.offsetHeight : 0;
      this.style.setProperty('--oudh-welcome-lift', `${lift}px`);
    };
    this.#barObserver = new MutationObserver(update);
    this.#barObserver.observe(bar, { attributes: true, attributeFilter: ['data-visible'] });
    this.#barResize = new ResizeObserver(update);
    this.#barResize.observe(bar);
    update();
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-close]')) this.dialog.close();
    else if (target.closest('[data-teaser-open]')) this.#open();
    else if (target.closest('[data-teaser-close]')) {
      this.#write({ teaserHidden: true });
      this.#hideTeaser();
    } else if (target.closest('[data-copy]')) this.#copy(target.closest('[data-copy]'));
  }

  async #tryOpen() {
    await this.#consentSettled();

    // Never stack on another modal (cart drawer, search).
    if (document.querySelector('dialog[open]')) {
      this.#timer = window.setTimeout(() => this.#tryOpen(), 8000);
      return;
    }
    this.#open();
  }

  /** @param {'form' | 'success'} [view] */
  #open(view = 'form') {
    this.#showView(view);
    this.#hideTeaser();
    if (!this.dialog.open) this.dialog.showModal();
  }

  #onClose() {
    if (window.Shopify?.designMode) return;
    const state = this.#read();
    if (!state.subscribed) {
      this.#write({ snoozeUntil: Date.now() + Number(this.dataset.days ?? 14) * 86400000 });
      if (!state.teaserHidden) this.#showTeaser();
    }
  }

  /** @param {string} name */
  #showView(name) {
    this.querySelectorAll('[data-view]').forEach((view) => {
      if (view instanceof HTMLElement) view.hidden = view.dataset.view !== name;
    });
  }

  #showTeaser() {
    if (this.teaser instanceof HTMLElement) this.teaser.hidden = false;
  }

  #hideTeaser() {
    if (this.teaser instanceof HTMLElement) this.teaser.hidden = true;
  }

  async #subscribe() {
    const error = this.querySelector('[data-error]');
    const button = this.form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;
    if (error instanceof HTMLElement) error.hidden = true;

    try {
      const response = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form) });
      if (!new URL(response.url).searchParams.has('customer_posted')) throw new Error('rejected');

      this.#write({ subscribed: true });
      this.#open('success');
    } catch {
      if (error instanceof HTMLElement) error.hidden = false;
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  }

  /** @param {Element | null} button */
  async #copy(button) {
    const code = this.dataset.code ?? '';
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // Clipboard is blocked outside secure contexts; select the code so Ctrl+C works.
      const node = this.querySelector('[data-code]');
      if (node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
      return;
    }

    if (!button) return;
    const label = button.textContent;
    button.textContent = 'Copied';
    window.setTimeout(() => {
      button.textContent = label;
    }, 2000);
  }

  /** @returns {{ snoozeUntil: number, subscribed?: boolean, teaserHidden?: boolean }} */
  #read() {
    try {
      return { snoozeUntil: 0, ...JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') };
    } catch {
      return { snoozeUntil: 0 };
    }
  }

  /** @param {Record<string, unknown>} changes */
  #write(changes) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.#read(), ...changes }));
    } catch {
      // Storage can be blocked; the popup then simply shows again next visit.
    }
  }
}

if (!customElements.get('oudh-welcome-popup')) {
  customElements.define('oudh-welcome-popup', OudhWelcomePopup);
}
